import { z } from "zod";
import { dateStringSchema, txStatusSchema, txExecutionResultSchema } from "./common.js";
export const murderBoardTxSchema = z.object({
    txHash: z.string(),
    blockNumber: z.number().nullable(),
    status: txStatusSchema,
    executionResult: txExecutionResultSchema.nullable(),
    actualFee: z.string().nullable(),
    roles: z.array(z.string()),
    clusterId: z.number().nullable(),
    clusterSize: z.number().nullable(),
    outlierScore: z.number().nullable(),
    feePayer: z.string(),
    numNoteHashes: z.number(),
    numNullifiers: z.number(),
    numL2ToL1Msgs: z.number(),
    numPrivateLogs: z.number(),
    numPublicLogs: z.number().nullable(),
    numSetupCalls: z.number(),
    numAppCalls: z.number(),
    hasTeardown: z.boolean(),
    totalPublicCalldataSize: z.number(),
    createdAt: dateStringSchema,
    firstSeenAt: dateStringSchema.nullable(),
    proposedAt: dateStringSchema.nullable(),
});
export const murderBoardClusterSchema = z.object({
    clusterId: z.number(),
    clusterSize: z.number(),
    txCount: z.number(),
});
export const murderBoardFpcSchema = z.object({
    address: z.string(),
    label: z.string().nullable(),
    contractType: z.string().nullable(),
    txCount: z.number(),
    networkShare: z.number(),
    // True when this address is the only one using this FPC (no anonymity provided).
    isExclusive: z.boolean(),
});
export const privacyScoreFactorSchema = z.object({
    name: z.string(),
    impact: z.enum(["good", "bad", "neutral"]),
    detail: z.string(),
});
// Unified address interaction: any address that appears in any role in this address's txs.
export const addressInteractionSchema = z.object({
    address: z.string(),
    label: z.string().nullable(),
    contractType: z.string().nullable(),
    coOccurrences: z.number(), // distinct txs this address appears in
    roles: z.array(z.string()), // distinct roles: callee, msgSender, calldata, l2ToL1Recipient, l2ToL1Sender
    phases: z.array(z.string()), // distinct phases seen (setup, app, teardown)
    functionSelectors: z.array(z.string()), // distinct selectors (callee role only)
});
// Repeated storage slot: a leaf slot written by more than one of the address's txs.
export const repeatedSlotSchema = z.object({
    leafSlot: z.string(),
    txCount: z.number(),
    resolvedLabel: z.string().nullable(),
    contractAddress: z.string().nullable(),
});
export const murderBoardResponseSchema = z.object({
    address: z.string(),
    totalTxs: z.number(),
    networkTxCount: z.number(),
    latestRunId: z.number().nullable(),
    transactions: z.array(murderBoardTxSchema),
    clusters: z.array(murderBoardClusterSchema),
    fpcsUsed: z.array(murderBoardFpcSchema),
    privacyScore: z
        .object({
        score: z.number(),
        factors: z.array(privacyScoreFactorSchema),
    })
        .nullable(),
    // 24-bucket activity histogram: index = UTC hour (0–23), value = tx count
    activityByHour: z.array(z.number()),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
});
export const addressInteractionsResponseSchema = z.object({
    addressInteractions: z.array(addressInteractionSchema),
});
export const repeatedSlotsResponseSchema = z.object({
    repeatedSlots: z.array(repeatedSlotSchema),
});
export const murderBoardParamSchema = z.object({
    id: z.string(),
    address: z.string(),
});
export const murderBoardQuerySchema = z.object({
    page: z.string().optional(),
    limit: z.string().optional(),
    clusterId: z.string().optional(),
});
export const coTxsParamSchema = z.object({
    id: z.string(),
    address: z.string(),
    coAddress: z.string(),
});
export const coTxsQuerySchema = z.object({
    page: z.string().optional(),
    limit: z.string().optional(),
});
export const coTxsRowSchema = murderBoardTxSchema.extend({
    coRoles: z.array(z.string()),
});
export const coTxsResponseSchema = z.object({
    transactions: z.array(coTxsRowSchema),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
});
//# sourceMappingURL=murder-board.js.map